export interface NearMeCoordinates {
  latitude: number;
  longitude: number;
}

/** IP-derived city coordinates — same endpoint the customer app uses for its region fallback. */
const IP_LOCATION_URL = 'https://services.metropolis.io/where-am-i';

/**
 * A `timeout` is required: without one `getCurrentPosition` never calls back on
 * devices that can't get a fix (or when the visitor leaves the permission prompt
 * open), which leaves "Parking near me" hanging with no way out.
 */
const BROWSER_GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 5000,
  maximumAge: 60000,
};

function requestBrowserPosition(): Promise<NearMeCoordinates | null> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      () => resolve(null),
      BROWSER_GEOLOCATION_OPTIONS,
    );
  });
}

async function requestIpLocation(): Promise<NearMeCoordinates | null> {
  try {
    const response = await fetch(IP_LOCATION_URL);
    if (!response.ok) return null;
    const { lat, long } = (await response.json()) as { lat?: string; long?: string };
    if (!lat || !long) return null;
    const latitude = Number(lat);
    const longitude = Number(long);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    return { latitude, longitude };
  } catch {
    return null;
  }
}

/**
 * Coordinates for the hero's "Parking near me" search.
 *
 * Prefers a precise browser position and falls back to the visitor's IP city so
 * near-me still lands on a populated map when geolocation is blocked, missing or
 * too slow to answer. Resolves null only when both sources fail — the caller then
 * has to ask for an address.
 */
export async function resolveNearMeCoordinates(
  knownPosition?: NearMeCoordinates | null,
): Promise<NearMeCoordinates | null> {
  if (knownPosition) return knownPosition;
  return (await requestBrowserPosition()) ?? (await requestIpLocation());
}
